/**
 * SignalBars — compact, theme-aware cellular/radio signal indicator.
 *
 * Four ascending bars filled by signal quality (derived from dBm) and tinted
 * green/amber/red, beside an optional quality word (Strong/Good/Fair/Weak).
 * The raw dBm shows on hover. A modern MUI replacement for the legacy SCSS
 * SignalStrength bar chart — no global styles, colors come from the theme.
 *
 * Contains:
 * - SignalBars — the indicator (default export)
 */
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

const BAR_COUNT = 4;

/** Maps a dBm reading (when online) to { bars, labelKey, fallback, color }. */
function classifySignal(value, online) {
  if (!online || value == null || Number.isNaN(Number(value))) {
    return { bars: 0, labelKey: 'sites.signalNone', fallback: 'No signal', color: 'text.disabled' };
  }
  const dbm = Number(value);
  if (dbm >= -70) return { bars: 4, labelKey: 'sites.signalStrong', fallback: 'Strong', color: 'var(--status-ok)' };
  if (dbm >= -85) return { bars: 3, labelKey: 'sites.signalGood', fallback: 'Good', color: 'var(--status-ok)' };
  if (dbm >= -95) return { bars: 2, labelKey: 'sites.signalFair', fallback: 'Fair', color: 'var(--status-warning)' };
  return { bars: 1, labelKey: 'sites.signalWeak', fallback: 'Weak', color: 'var(--status-bad)' };
}

/**
 * @param {object}      props
 * @param {number|null} props.value       - last known signal strength in dBm
 * @param {boolean}     [props.online]     - whether the radio is online (offline → no signal)
 * @param {boolean}     [props.showLabel]  - render the quality word beside the bars
 */
function SignalBars({ value, online = true, showLabel = true }) {
  const { t } = useTranslation();
  const { bars, labelKey, fallback, color } = classifySignal(value, online);
  const label = t(labelKey, fallback);
  const hasDbm = value != null && !Number.isNaN(Number(value));
  const tooltip = hasDbm ? `${label} · ${Number(value)} dBm` : label;

  return (
    <Tooltip title={tooltip}>
      <Stack direction="row" alignItems="center" spacing={0.75} component="span" sx={{ cursor: 'default' }}>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: '2px', height: 16 }}>
          {Array.from({ length: BAR_COUNT }, (_, i) => (
            <Box
              key={i}
              component="span"
              sx={{
                width: 4,
                height: 4 + i * 4,
                borderRadius: '1px',
                bgcolor: i < bars ? color : 'action.disabledBackground',
              }}
            />
          ))}
        </Box>
        {showLabel ? (
          <Typography component="span" variant="body2" sx={{ fontWeight: 500, color }}>
            {label}
          </Typography>
        ) : null}
      </Stack>
    </Tooltip>
  );
}

SignalBars.propTypes = {
  value: PropTypes.number,
  online: PropTypes.bool,
  showLabel: PropTypes.bool,
};

export default SignalBars;
